// ========================================
// Collection Filters & Sorters Manager
// ========================================

const erjCollectionPanelState = {
  isSorterOpen: false,
  activeFilterItems: new Set(),
  initialFilterStates: new Map(),
  tempSelectedFilters: new Map(),
  initialPriceMin: null,
  initialPriceMax: null,
};

const ERJ_PRICE_PARAM_MIN = "filter.v.price.gte";
const ERJ_PRICE_PARAM_MAX = "filter.v.price.lte";
const ERJ_PRICE_USD_PARAM = "filter.p.m.custom.min_price_usd";

function erjGetFiltersSectionId() {
  const filtersSection =
    document.querySelector("[data-collection-filters-section]") ||
    document.getElementById("default-collection-v2-section") ||
    document.getElementById("product-grid");
  return filtersSection?.dataset?.id || null;
}

function erjComposeFilterUrl() {
  const baseUrl = window.location.pathname;
  const newParams = new URLSearchParams(window.location.search);

  const managedFilterParams = new Set([ERJ_PRICE_PARAM_MIN, ERJ_PRICE_PARAM_MAX]);
  erjCollectionPanelState.initialFilterStates.forEach((_, key) => {
    managedFilterParams.add(key.split("|")[0]);
  });
  erjCollectionPanelState.tempSelectedFilters.forEach((_, paramName) => {
    managedFilterParams.add(paramName);
  });

  managedFilterParams.forEach((paramName) => {
    newParams.delete(paramName);
  });

  erjCollectionPanelState.tempSelectedFilters.forEach((values, paramName) => {
    values.forEach((value) => {
      newParams.append(paramName, value);
    });
  });

  return `${baseUrl}?${newParams.toString()}`;
}

// Build a collection request URL with optional section rendering and non-USD price tiers.
window.erjRewritePriceParamsForRequest = function (urlStr, sectionId) {
  try {
    const u = new URL(urlStr, window.location.origin);
    if (sectionId) u.searchParams.set("section_id", sectionId);

    const shopifyCurrency = (window.Shopify && window.Shopify.currency) || {};
    const activeCurrency = shopifyCurrency.active || "USD";
    if (activeCurrency === "USD") return u.toString();

    const gte = u.searchParams.get(ERJ_PRICE_PARAM_MIN);
    const lte = u.searchParams.get(ERJ_PRICE_PARAM_MAX);
    if (gte === null && lte === null) return u.toString();

    const data = window.erjPriceFilterData || {};
    const gteVal = gte !== null ? gte : String(parseFloat(data.rangeMin) || 0);
    const lteVal = lte !== null ? lte : String(data.rangeMax);

    const rate = parseFloat(shopifyCurrency.rate) || 1;
    const usdMin = parseFloat(gteVal) / rate;
    const usdMax = parseFloat(lteVal) / rate;
    if (!Number.isFinite(usdMin) || !Number.isFinite(usdMax) || usdMax <= 0) return u.toString();

    const ceilEven = (v) => Math.ceil(v / 2) * 2;
    const start = Math.max(2, ceilEven(usdMin));
    const end = ceilEven(usdMax);
    if (end < start) return u.toString();

    const tiers = [];
    for (let v = start; v <= end; v += 2) {
      tiers.push(String(v));
    }

    u.searchParams.delete(ERJ_PRICE_PARAM_MIN);
    u.searchParams.delete(ERJ_PRICE_PARAM_MAX);
    u.searchParams.delete(ERJ_PRICE_USD_PARAM);
    tiers.forEach((tier) => {
      u.searchParams.append(ERJ_PRICE_USD_PARAM, tier);
    });

    erjRememberPriceRange(tiers, gteVal, lteVal);
    return u.toString();
  } catch (e) {
    return urlStr;
  }
};

// Cache local bounds by tier set to avoid lossy reverse conversion.
const ERJ_PRICE_RANGE_MEMORY_KEY = "erjPriceRangeMemory";
function erjRememberPriceRange(tiers, gte, lte) {
  try {
    const memory = JSON.parse(sessionStorage.getItem(ERJ_PRICE_RANGE_MEMORY_KEY) || "{}");
    memory[erjPriceRangeMemoryKeyForTiers(tiers)] = { gte: gte, lte: lte };
    sessionStorage.setItem(ERJ_PRICE_RANGE_MEMORY_KEY, JSON.stringify(memory));
  } catch (e) {
    /* storage unavailable — arithmetic fallback still applies */
  }
}

function erjRecallPriceRange(tiers) {
  try {
    const memory = JSON.parse(sessionStorage.getItem(ERJ_PRICE_RANGE_MEMORY_KEY) || "{}");
    return memory[erjPriceRangeMemoryKeyForTiers(tiers)] || null;
  } catch (e) {
    return null;
  }
}

function erjPriceRangeMemoryKeyForTiers(tiers) {
  return tiers.slice().sort(function (a, b) { return a - b; }).join(",");
}

// Remove cards outside the local price range using the first variant price.
// Default to the address-bar URL; keep cards with unreadable prices.
function erjFilterProductCardsByPriceRange(scope, queryUrl) {
  let params;
  try {
    params = new URL(
      queryUrl || window.location.href,
      window.location.origin
    ).searchParams;
  } catch (e) {
    return;
  }

  const gteStr = params.get(ERJ_PRICE_PARAM_MIN);
  const lteStr = params.get(ERJ_PRICE_PARAM_MAX);
  if (gteStr === null && lteStr === null) return;

  const gte = gteStr !== null ? parseFloat(gteStr) : -Infinity;
  const lte = lteStr !== null ? parseFloat(lteStr) : Infinity;

  scope.querySelectorAll("div.productitem[data-product]").forEach((card) => {
    const price = parseFloat(card.getAttribute("data-first-variant-price"));
    if (Number.isFinite(price) && (price < gte || price > lte)) {
      card.remove();
    }
  });
}

window.erjFilterProductCardsByPriceRange = erjFilterProductCardsByPriceRange;

function isFilterPanelOpen() {
  return document.querySelector(".erj-collection-filter-panel-overlay")?.classList.contains("active")
}

// Global manager instances for cross-communication
let erjCollectionSorterManager = null;
let erjCollectionFilterManager = null;

// Utility Functions
function _debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Global control methods for mutual exclusion
function erjCollectionCloseSorter() {
  if (erjCollectionSorterManager && isFilterPanelOpen()) {
    erjCollectionSorterManager.closeSorter();
  }
}

function erjCollectionCloseFilter() {
  if (erjCollectionFilterManager && isFilterPanelOpen()) {
    erjCollectionFilterManager.closePanel();
  }
}

// ========================================
// Sorter Management Class
// ========================================
class ErcSorterManager {
  constructor() {
    this.searchParams = new URLSearchParams(window.location.search);
    this.sortParam = this.searchParams.get("sort_by");
    this.initElements();
    this.bindEvents();
    this.setSelectedOption();
    this.boundHandleDocumentClick = this._handleDocumentClick.bind(this);
  }

  initElements() {
    this.erjCollectionSorter = document.querySelector(".erj-collection-sorter");
    if (!this.erjCollectionSorter) {
      return;
    }

    this.erjCollectionSorterTitle = this.erjCollectionSorter.querySelector(
      ".erj-collection-sorter-title"
    );
    this.erjCollectionSorterDropdown = this.erjCollectionSorter.querySelector(
      ".erj-collection-sorter-dropdown"
    );
    this.erjCollectionSorterOptions =
      this.erjCollectionSorterDropdown?.querySelectorAll("li");
  }

  bindEvents() {
    if (!this.erjCollectionSorterTitle || !this.erjCollectionSorterDropdown)
      return;

    this.erjCollectionSorterTitle.addEventListener("click", () => {
      this.toggleDropdown();
    });

    this.erjCollectionSorterDropdown.querySelector("ul")?.addEventListener(
      "click",
      (e) => {
        const target = e.target;

        if (
          !target?.classList?.contains("erj-collection-sorter-dropdown-item")
        ) {
          return;
        }

        const sortParam = target.dataset.value;
        this.updateURL(sortParam);
      }
    );

    document.addEventListener("click", this.boundHandleDocumentClick, true);
  }

  _handleDocumentClick(event) {
    if (
      erjCollectionPanelState.isSorterOpen &&
      this.erjCollectionSorter &&
      !this.erjCollectionSorter.contains(event.target)
    ) {
      this.closeSorter();
    }
  }

  _animateOpen() {
    const dropdown = this.erjCollectionSorterDropdown;
    if (!dropdown) return;

    dropdown.style.height = "auto";
    const fullHeight = dropdown.scrollHeight;
    dropdown.style.height = "0px";
    // Force a reflow instead of nesting requestAnimationFrame: double-rAF
    // never resolves in render-suspended webviews (e.g. occluded preview
    // panes), which left the dropdown stuck at opacity 0.
    void dropdown.offsetHeight;
    dropdown.style.height = `${fullHeight}px`;
    dropdown.style.opacity = "1";
  }

  _animateClose() {
    const dropdown = this.erjCollectionSorterDropdown;
    if (!dropdown) return;

    dropdown.style.height = `${dropdown.scrollHeight}px`;
    void dropdown.offsetHeight;
    dropdown.style.height = "0px";
    dropdown.style.opacity = "0";
  }

  toggleDropdown() {
    const dropdown = this.erjCollectionSorterDropdown;
    if (!dropdown) return;

    if (!erjCollectionPanelState.isSorterOpen) {
      erjCollectionCloseFilter();
      erjCollectionPanelState.isSorterOpen = true;
      dropdown.classList.add("active");
      this.erjCollectionSorterTitle?.classList.add("active");
      this._animateOpen();
      document.addEventListener("click", this.boundHandleDocumentClick, true);
    } else {
      this.closeSorter();
    }
  }

  closeSorter() {
    const dropdown = this.erjCollectionSorterDropdown;

    this.erjCollectionSorterTitle?.classList.remove("active");

    if (!erjCollectionPanelState.isSorterOpen) {
      document.removeEventListener(
        "click",
        this.boundHandleDocumentClick,
        true
      );
      if (dropdown && dropdown.classList.contains("active")) {
        dropdown.classList.remove("active");
        dropdown.style.height = "0px";
        dropdown.style.opacity = "0";
      }
      return;
    }

    erjCollectionPanelState.isSorterOpen = false;
    document.removeEventListener("click", this.boundHandleDocumentClick, true);

    if (dropdown) {
      this._animateClose();
      dropdown.classList.remove("active");
    }
  }

  updateURL(sortParam) {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set("sort_by", sortParam);
    window.location.href = newUrl.toString();
  }

  setSelectedOption() {
    if (!this.erjCollectionSorterOptions) return;

    [...this.erjCollectionSorterOptions].forEach((option) => {
      if (option.dataset.value === this.sortParam) {
        option.classList.add("selected");
      }
    });
  }
}

// ========================================
// Filter Panel Management Class
// ========================================
class ErcFilterPanelManager {
  constructor() {
    this.initElements();
    this.bindEvents();
  }

  initElements() {
    this.erjCollectionFilterToggle = document.querySelector(
      ".erj-collection-filter-title"
    );
    this.erjCollectionFilterOverlay = document.querySelector(
      ".erj-collection-filter-panel-overlay"
    );
    this.erjCollectionFilterPanel =
      this.erjCollectionFilterOverlay?.querySelector(
        ".erj-collection-filter-panel"
      );
    this.erjCollectionFilterClose =
      this.erjCollectionFilterPanel?.querySelector(
        ".erj-collection-filter-panel-close"
      );
    this.erjCollectionFilterReset =
      this.erjCollectionFilterPanel?.querySelector(
        ".erj-collection-filter-panel-reset"
      );
    this.erjCollectionFilterApply =
      this.erjCollectionFilterPanel?.querySelector(
        ".erj-collection-filter-panel-footer-apply"
      );

    this.priceRangeMinEl = document.getElementById("price-range-min");
    this.priceRangeMaxEl = document.getElementById("price-range-max");
    this.priceInputMinEl = document.getElementById("price-input-min");
    this.priceInputMaxEl = document.getElementById("price-input-max");
    this.priceRangeSelectedTrackEl = document.querySelector(
      ".price-range-selected-track"
    );
  }

  updatePriceVisuals(minVal, maxVal, rangeMin, rangeMax) {
    if (
      !this.priceRangeMinEl ||
      !this.priceRangeMaxEl ||
      !this.priceInputMinEl ||
      !this.priceInputMaxEl ||
      !this.priceRangeSelectedTrackEl
    ) {
      return;
    }
    const min = parseFloat(minVal);
    const max = parseFloat(maxVal);
    const rMin = parseFloat(rangeMin) || 0;
    const rMax = parseFloat(rangeMax);

    this.priceRangeMinEl.value = min;
    this.priceRangeMaxEl.value = max;
    this.priceInputMinEl.value = min;
    this.priceInputMaxEl.value = max;

    const span = rMax - rMin;
    if (span > 0) {
      const leftPercent = Math.round(((min - rMin) / span) * 1000) / 10;
      const widthPercent = Math.round(((max - min) / span) * 1000) / 10;
      this.priceRangeSelectedTrackEl.style.left = `${leftPercent}%`;
      this.priceRangeSelectedTrackEl.style.width = `${widthPercent}%`;
    } else {
      this.priceRangeSelectedTrackEl.style.left = "0%";
      this.priceRangeSelectedTrackEl.style.width = "0%";
    }
  }

  bindPriceFilterEvents() {
    if (
      !this.priceRangeMinEl ||
      !this.priceRangeMaxEl ||
      !this.priceInputMinEl ||
      !this.priceInputMaxEl
    ) {
      return;
    }

    const handleRangeInput = (event) => {
      let minVal = parseFloat(this.priceRangeMinEl.value);
      let maxVal = parseFloat(this.priceRangeMaxEl.value);
      const currentRangeMin = parseFloat(this.priceRangeMinEl.min) || 0;
      const currentRangeMax = parseFloat(this.priceRangeMinEl.max);

      const activeEl = event.target;

      if (activeEl === this.priceRangeMinEl && minVal > maxVal) {
        maxVal = minVal;
        this.priceRangeMaxEl.value = maxVal;
      } else if (activeEl === this.priceRangeMaxEl && maxVal < minVal) {
        minVal = maxVal;
        this.priceRangeMinEl.value = minVal;
      }

      this.updatePriceVisuals(minVal, maxVal, currentRangeMin, currentRangeMax);
      erjCollectionPanelState.tempSelectedFilters.set(
        ERJ_PRICE_PARAM_MIN,
        new Set([minVal.toString()])
      );
      erjCollectionPanelState.tempSelectedFilters.set(
        ERJ_PRICE_PARAM_MAX,
        new Set([maxVal.toString()])
      );
    };

    const handleNumberInput = (event) => {
      let minVal = parseFloat(this.priceInputMinEl.value);
      let maxVal = parseFloat(this.priceInputMaxEl.value);
      const currentRangeMin = parseFloat(this.priceRangeMinEl.min) || 0;
      const currentRangeMax = parseFloat(this.priceRangeMinEl.max);

      if (isNaN(minVal)) minVal = currentRangeMin;
      if (isNaN(maxVal)) maxVal = currentRangeMax;

      minVal = Math.max(currentRangeMin, Math.min(minVal, currentRangeMax));
      maxVal = Math.max(currentRangeMin, Math.min(maxVal, currentRangeMax));

      if (minVal > maxVal) {
        if (event.target === this.priceInputMinEl) {
          maxVal = minVal;
        } else {
          minVal = maxVal;
        }
      }
      this.updatePriceVisuals(minVal, maxVal, currentRangeMin, currentRangeMax);
      erjCollectionPanelState.tempSelectedFilters.set(
        ERJ_PRICE_PARAM_MIN,
        new Set([minVal.toString()])
      );
      erjCollectionPanelState.tempSelectedFilters.set(
        ERJ_PRICE_PARAM_MAX,
        new Set([maxVal.toString()])
      );
    };

    this.priceRangeMinEl.addEventListener(
      "input",
      _debounce(handleRangeInput, 10)
    );
    this.priceRangeMaxEl.addEventListener(
      "input",
      _debounce(handleRangeInput, 10)
    );
    this.priceInputMinEl.addEventListener("blur", handleNumberInput);
    this.priceInputMaxEl.addEventListener("blur", handleNumberInput);
  }

  bindEvents() {
    if (!this.erjCollectionFilterToggle || !this.erjCollectionFilterOverlay)
      return;

    this.erjCollectionFilterToggle.addEventListener("click", () => {
      this.openPanel();
    });

    this.erjCollectionFilterOverlay.addEventListener("click", (e) => {
      if (e.target === this.erjCollectionFilterOverlay) {
        this.applyFilters();
        this.closePanel(false);
      }
    });

    this.erjCollectionFilterClose?.addEventListener("click", () => {
      this.closePanel(false);
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && isFilterPanelOpen()) {
        this.closePanel(false);
      }
    });

    this.erjCollectionFilterReset?.addEventListener("click", () => {
      this.resetFilters();
    });

    this.erjCollectionFilterApply?.addEventListener("click", () => {
      this.applyFilters();
    });

    this.bindPriceFilterEvents();
    this.seedPriceFilterFromUrl();
  }

  // Initialize non-USD slider bounds and values from config and URL params.
  seedPriceFilterFromUrl() {
    if (!this.priceRangeMinEl || !this.priceRangeMaxEl) return;
    const data = window.erjPriceFilterData;
    if (!data) return;
    const rangeMin = parseFloat(data.rangeMin) || 0;
    const rangeMax = parseFloat(data.rangeMax);
    if (!(rangeMax > rangeMin)) return;

    this.priceRangeMinEl.min = rangeMin;
    this.priceRangeMinEl.max = rangeMax;
    this.priceRangeMaxEl.min = rangeMin;
    this.priceRangeMaxEl.max = rangeMax;
    if (this.priceInputMinEl) {
      this.priceInputMinEl.min = rangeMin;
      this.priceInputMinEl.max = rangeMax;
    }
    if (this.priceInputMaxEl) {
      this.priceInputMaxEl.min = rangeMin;
      this.priceInputMaxEl.max = rangeMax;
    }

    // AJAX leaves the config script unchanged; refresh URL-seeded values.
    if (data.currentMin !== undefined && !data.seededFromUrl) return;

    const params = new URLSearchParams(window.location.search);
    const gte = parseFloat(params.get(ERJ_PRICE_PARAM_MIN));
    const lte = parseFloat(params.get(ERJ_PRICE_PARAM_MAX));

    let min = rangeMin;
    let max = rangeMax;
    if (!isNaN(gte) || !isNaN(lte)) {
      if (!isNaN(gte)) min = Math.max(rangeMin, Math.min(gte, rangeMax));
      if (!isNaN(lte)) max = Math.max(rangeMin, Math.min(lte, rangeMax));
    }

    data.currentMin = min;
    data.currentMax = max;
    data.seededFromUrl = true;
    this.updatePriceVisuals(min, max, rangeMin, rangeMax);
  }

  storeInitialFilterStates() {
    erjCollectionPanelState.initialFilterStates.clear();
    const checkboxButtons = document.querySelectorAll(
      ".erj-collection-filter-panel-filter-item-checkbox-item button"
    );
    checkboxButtons.forEach((button) => {
      const paramName = button.dataset.filterParamName;
      const value = button.dataset.filterValue;
      if (paramName && value) {
        const key = `${paramName}|${value}`;
        erjCollectionPanelState.initialFilterStates.set(
          key,
          button.classList.contains("active")
        );
      }
    });
    if (this.priceInputMinEl && this.priceInputMaxEl) {
      erjCollectionPanelState.initialPriceMin = parseFloat(
        this.priceInputMinEl.value
      );
      erjCollectionPanelState.initialPriceMax = parseFloat(
        this.priceInputMaxEl.value
      );
    }
  }

  resetTempSelectedFilters() {
    erjCollectionPanelState.tempSelectedFilters.clear();
    erjCollectionPanelState.initialFilterStates.forEach((isActive, key) => {
      if (isActive) {
        const [paramName, value] = key.split("|");
        if (!erjCollectionPanelState.tempSelectedFilters.has(paramName)) {
          erjCollectionPanelState.tempSelectedFilters.set(paramName, new Set());
        }
        erjCollectionPanelState.tempSelectedFilters.get(paramName).add(value);
      }
    });
  }

  restoreFilterStatesToInitial() {
    const checkboxButtons = document.querySelectorAll(
      ".erj-collection-filter-panel-filter-item-checkbox-item button"
    );
    checkboxButtons.forEach((button) => {
      const paramName = button.dataset.filterParamName;
      const value = button.dataset.filterValue;
      if (paramName && value) {
        const key = `${paramName}|${value}`;
        const isActiveInitial =
          erjCollectionPanelState.initialFilterStates.get(key);
        if (isActiveInitial) {
          button.classList.add("active");
          button.setAttribute("aria-checked", "true");
        } else {
          button.classList.remove("active");
          button.setAttribute("aria-checked", "false");
        }
      }
    });
    this.resetTempSelectedFilters();
  }

  openPanel() {
    if (isFilterPanelOpen()) return;
    let filterLayout = document.querySelector(".erj-collection-filter-panel")
    let windowHeight = window.innerHeight;
    filterLayout.style.height = (windowHeight) + 'px';
    
    erjCollectionCloseSorter();

    this.storeInitialFilterStates();
    this.resetTempSelectedFilters();

    if (window.erjPriceFilterData && this.priceRangeMinEl) {
      const { rangeMin, rangeMax, currentMin, currentMax } = window.erjPriceFilterData;
      const rMin = parseFloat(rangeMin) || 0;
      const rMax = parseFloat(rangeMax);
      this.priceRangeMinEl.min = rMin;
      this.priceRangeMinEl.max = rMax;
      this.priceRangeMaxEl.min = rMin;
      this.priceRangeMaxEl.max = rMax;
      if (this.priceInputMinEl) {
        this.priceInputMinEl.min = rMin;
        this.priceInputMinEl.max = rMax;
      }
      if (this.priceInputMaxEl) {
        this.priceInputMaxEl.min = rMin;
        this.priceInputMaxEl.max = rMax;
      }

      this.updatePriceVisuals(currentMin, currentMax, rMin, rMax);

      const minP = parseFloat(currentMin);
      const maxP = parseFloat(currentMax);

      if (minP > rMin || maxP < rMax) {
        erjCollectionPanelState.tempSelectedFilters.set(
          ERJ_PRICE_PARAM_MIN,
          new Set([minP.toString()])
        );
        erjCollectionPanelState.tempSelectedFilters.set(
          ERJ_PRICE_PARAM_MAX,
          new Set([maxP.toString()])
        );
      }
    }

    this.erjCollectionFilterOverlay.classList.add("active");
    this.erjCollectionFilterToggle?.classList.add("active");
    this.lockBodyScroll();
  }

  closePanel(isApplyingFilters = false) {
    if (!isFilterPanelOpen()) return;

    if (!isApplyingFilters) {
      this.restoreFilterStatesToInitial();
      if (
        this.priceRangeMinEl &&
        erjCollectionPanelState.initialPriceMin !== null &&
        erjCollectionPanelState.initialPriceMax !== null
      ) {
        const rangeMin = parseFloat(this.priceRangeMinEl.min) || 0;
        const rangeMax = parseFloat(this.priceRangeMinEl.max);
        this.updatePriceVisuals(
          erjCollectionPanelState.initialPriceMin,
          erjCollectionPanelState.initialPriceMax,
          rangeMin,
          rangeMax
        );
        if (
          erjCollectionPanelState.initialPriceMin > rangeMin ||
          erjCollectionPanelState.initialPriceMax < rangeMax
        ) {
          erjCollectionPanelState.tempSelectedFilters.set(
            ERJ_PRICE_PARAM_MIN,
            new Set([erjCollectionPanelState.initialPriceMin.toString()])
          );
          erjCollectionPanelState.tempSelectedFilters.set(
            ERJ_PRICE_PARAM_MAX,
            new Set([erjCollectionPanelState.initialPriceMax.toString()])
          );
        } else {
          erjCollectionPanelState.tempSelectedFilters.delete(
            ERJ_PRICE_PARAM_MIN
          );
          erjCollectionPanelState.tempSelectedFilters.delete(
            ERJ_PRICE_PARAM_MAX
          );
        }
      }
    }

    this.erjCollectionFilterOverlay.classList.remove("active");
    this.erjCollectionFilterToggle?.classList.remove("active");
    this.unlockBodyScroll();
  }

  togglePanel() {
    if (isFilterPanelOpen()) {
      this.closePanel(false);
    } else {
      this.openPanel();
    }
  }

  handleEscapeKey(e) {
    if (e.key === "Escape" && isFilterPanelOpen()) {
      this.closePanel(false);
    }
  }

  lockBodyScroll() {
    document.body.style.overflow = "hidden";
  }

  unlockBodyScroll() {
    document.body.style.overflow = "";
  }

  resetFilters() {
    const checkboxButtons = document.querySelectorAll(
      ".erj-collection-filter-panel-filter-item-checkbox-item button"
    );
    checkboxButtons.forEach((button) => {
      button.classList.remove("active");
      button.setAttribute("aria-checked", "false");
    });
    erjCollectionPanelState.tempSelectedFilters.clear();

    if (this.priceRangeMinEl && window.erjPriceFilterData) {
      const defaultMin = parseFloat(window.erjPriceFilterData.rangeMin) || 0;
      const defaultMax = window.erjPriceFilterData.rangeMax;
      this.updatePriceVisuals(defaultMin, defaultMax, defaultMin, defaultMax);
    }

    const baseUrl = window.location.pathname;
    const currentParams = new URLSearchParams(window.location.search);
    let finalUrl = baseUrl;
    if (currentParams.has("sort_by")) {
      finalUrl += `?sort_by=${currentParams.get("sort_by")}`;
    }

    history.replaceState({}, document.title, finalUrl);
    this.searchAndRender(finalUrl);
    this.closePanel(true);
  }

  applyFilters() {
    if (
      this.priceInputMinEl &&
      this.priceInputMaxEl &&
      window.erjPriceFilterData
    ) {
      const minVal = parseFloat(this.priceInputMinEl.value);
      const maxVal = parseFloat(this.priceInputMaxEl.value);
      const rangeMin = parseFloat(window.erjPriceFilterData.rangeMin) || 0;
      const rangeMax = parseFloat(window.erjPriceFilterData.rangeMax);
      if (minVal === rangeMin && maxVal === rangeMax) {
        erjCollectionPanelState.tempSelectedFilters.delete(
          ERJ_PRICE_PARAM_MIN
        );
        erjCollectionPanelState.tempSelectedFilters.delete(
          ERJ_PRICE_PARAM_MAX
        );
      }
    }

    const newUrl = erjComposeFilterUrl();
    history.replaceState({}, document.title, newUrl);
    this.searchAndRender(newUrl);
    this.closePanel(true);
  }

  searchAndRender(queryUrl) {
    fetch(window.erjRewritePriceParamsForRequest(queryUrl, erjGetFiltersSectionId()))
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        erjFilterProductCardsByPriceRange(doc, queryUrl);
        const productGrid = document.getElementsByClassName("erj-collection-wrapper")?.[0];
        const newProductGrid = doc.getElementsByClassName("erj-collection-wrapper")?.[0];
        if (!newProductGrid?.innerHTML) {
          return;
        }

        const curTitleHTML = productGrid.querySelector(".erj-collection-title")?.innerHTML;

        productGrid.innerHTML = newProductGrid.innerHTML;

        if (curTitleHTML) {
          const newTitleEl = productGrid.querySelector(".erj-collection-title");
          if (newTitleEl) newTitleEl.innerHTML = curTitleHTML;
        }

        if (typeof window._erj_ !== 'undefined' && typeof window._erj_.initPromoSlotsPopup === 'function') {
          window._erj_.initPromoSlotsPopup();
        }
        if (typeof window._erj_ !== 'undefined' && typeof window._erj_.initCreatorCards === 'function') {
          window._erj_.initCreatorCards();
        }

        _erjCollectionInit();
        runAsyncActions();
        setLoadMore?.();
        if (typeof initVariantPickersInNewItems === "function") initVariantPickersInNewItems();
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }
}

// ========================================
// Filter Item Management Class
// ========================================
class ErcFilterItemManager {
  constructor() {
    this.initElements();
    this.bindEvents();
  }

  initElements() {
    this.erjCollectionFilterItems = document.querySelectorAll(
      ".erj-collection-filter-panel-filter-item"
    );
    this.erjCollectionFilterCheckboxOptions = document.querySelectorAll(
      ".erj-collection-filter-panel-filter-item-checkbox-item"
    );
    this.erjCollectionFilterShowMore = document.querySelectorAll(".show-more-btn");
    this.erjCollectionFilterShowLess = document.querySelectorAll(".show-less-btn");
  }

  bindEvents() {
    this.erjCollectionFilterItems.forEach((item) => {
      const itemTitle = item.querySelector(
        ".erj-collection-filter-panel-filter-item-title"
      );

      if (!itemTitle) return;

      itemTitle.addEventListener(
        "click",
        () => {
          this.toggleItem(item);
        }
      );
    });

    this.erjCollectionFilterCheckboxOptions.forEach((option) => {
      option.addEventListener("click", (event) => {
        const button = event.currentTarget.querySelector("button");
        this.handleCheckboxClick(button);
      });
    });

    this.erjCollectionFilterShowMore.forEach(btn=>{
      btn.addEventListener("click", event => {
        this.showMoreEvent(btn)
      })
    })

    this.erjCollectionFilterShowLess.forEach(btn => {
      btn.addEventListener("click", event =>{
        this.showLessEvent(btn)
      })
    })
    
  }

  handleCheckboxClick(button) {
    const paramName = button.dataset.filterParamName;
    const value = button.dataset.filterValue;

    button.classList.toggle("active");
    button.setAttribute("aria-checked", button.classList.contains("active"));

    if (!erjCollectionPanelState.tempSelectedFilters.has(paramName)) {
      erjCollectionPanelState.tempSelectedFilters.set(paramName, new Set());
    }

    if (button.classList.contains("active")) {
      erjCollectionPanelState.tempSelectedFilters.get(paramName).add(value);
    } else {
      erjCollectionPanelState.tempSelectedFilters.get(paramName).delete(value);
    }

    this.updateOptions()
  }

  updateFilterContentHeight(checkboxItem) {
    const itemContent = checkboxItem?.parentNode;

    if (!itemContent?.style?.height) {
      return;
    }

    itemContent.style.height = "auto";
  }

  updateOptions() {
    const newUrl = erjComposeFilterUrl();

    fetch(window.erjRewritePriceParamsForRequest(newUrl, erjGetFiltersSectionId()))
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const newFilterItems = doc.querySelectorAll(".erj-collection-filter-panel-filter-item");
        const currentFilterItems = document.querySelectorAll(".erj-collection-filter-panel-filter-item");

        currentFilterItems.forEach((item) => {
          const title = item.dataset.title;
          const filterItem = newFilterItems.values().find((newItem) => {
            return newItem.dataset.title === title;
          });

          if (!filterItem) {
            return;
          }
          
          if (filterItem.classList.contains("hidden")) {
            item.classList.add("hidden");
          } else {
            item.classList.remove("hidden");
          }

          const newShowMoreBtn = filterItem.querySelector(".show-more-btn");
          const currentShowMoreBtn = item.querySelector(".show-more-btn");
          const currentShowLessBtn = currentShowMoreBtn?.parentElement?.querySelector(".show-less-btn")
          if (newShowMoreBtn?.classList?.contains("hidden")){
            // reset show more
            this.showLessEvent(currentShowLessBtn);
            currentShowMoreBtn?.classList?.add("hidden")
          } else if (currentShowLessBtn?.classList?.contains("hidden")){
            currentShowMoreBtn?.classList?.remove("hidden")
          }
        });

        const newCheckboxItems = doc.querySelectorAll(".erj-collection-filter-panel-filter-item-checkbox-item");
        const currentCheckboxItems = document.querySelectorAll(".erj-collection-filter-panel-filter-item-checkbox-item");
        currentCheckboxItems.forEach((item) => {
          const value = item.dataset.value;
          const checkboxItem = newCheckboxItems.values().find((newItem) => {
            return newItem.dataset.value === value;
          });

          if (!checkboxItem) {
            return;
          }
          
          if (checkboxItem.classList.contains("hidden")) {
            item.classList.add("hidden");
          } else {
            item.classList.remove("hidden");
          }

          item.querySelector('.erj-collection-filter-panel-filter-item-count').innerHTML = checkboxItem.querySelector(".erj-collection-filter-panel-filter-item-count").innerHTML;

          this.updateFilterContentHeight(item);
        });
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
    
  }

  getItemId(item) {
    return (
      item?.dataset?.filterId ||
      item?.querySelector(".erj-collection-filter-panel-filter-item-title")
        ?.textContent ||
      "unknown"
    );
  }

  expandItemWithRAF(item, filterContent,startHeight = 0) {
    const itemId = this.getItemId(item);

    filterContent.style.height = "auto";

    requestAnimationFrame(() => {
      const fullHeight = filterContent.scrollHeight;
      filterContent.style.height = startHeight + "px";
      requestAnimationFrame(() => {
        filterContent.style.height = fullHeight + "px";
        filterContent.style.opacity = "1";
      });
    });
  }

  collapseItemWithRAF(item, filterContent,endHeight = 0) {
    const itemId = this.getItemId(item);

    filterContent.style.height = endHeight + "px";
    filterContent.style.opacity = "0";
  }

  toggleItem(item) {
    const filterContent = item.querySelector(
      ".erj-collection-filter-panel-filter-item-content"
    );
    if (!filterContent) return;

    const isActive = item.classList.contains("active");
    const itemId = this.getItemId(item);
    if (isActive) {
      this.collapseItemWithRAF(item, filterContent);
      erjCollectionPanelState.activeFilterItems.delete(itemId);
    } else {
      this.expandItemWithRAF(item, filterContent);
      erjCollectionPanelState.activeFilterItems.add(itemId);
    }

    item.classList.toggle("active");
  }

  showMoreEvent (btn) {
    btn.classList?.add("hidden");
    btn.parentElement?.querySelector(".show-less-btn")?.classList?.remove("hidden");
    btn.parentElement?.querySelectorAll(".show-more-item")?.forEach(el => {
      el.style.display = 'flex';
      el.style.height = 'auto';
    })

    const filterContent = btn.parentElement;
    if (filterContent) {
      this.expandItemWithRAF(_,filterContent,filterContent.style.height);
    }
  }
  
  showLessEvent (btn) {
    btn.classList?.add("hidden");
    btn.parentElement?.querySelector(".show-more-btn")?.classList?.remove("hidden");
    const options = btn.parentElement?.querySelectorAll(".show-more-item");
    options.forEach(el => {
      el.style.display = 'none';
    })
    const filterContent = btn.parentElement;
    if (filterContent) {
      filterContent.style.height = "fit-content";
    }
  }
}

// Render the non-USD price chip from local-currency URL params.
function erjRenderPriceFilterChip() {
  const shopifyCurrency = (window.Shopify && window.Shopify.currency) || {};
  const activeCurrency = shopifyCurrency.active || "USD";
  // USD already gets the native selected-filters chip from the Liquid side.
  if (activeCurrency === "USD") return;

  const container = document.querySelector(".erj-collection-selected-filters");
  if (!container) return;
  if (container.querySelector("[data-erj-price-chip]")) return;

  const params = new URLSearchParams(window.location.search);
  const gte = params.get(ERJ_PRICE_PARAM_MIN);
  const lte = params.get(ERJ_PRICE_PARAM_MAX);
  if (gte === null && lte === null) return;

  const data = window.erjPriceFilterData;
  const symbol = (data && data.currencySymbol) || "";
  const rangeMin = (data && parseFloat(data.rangeMin)) || 0;
  const rangeMax = data ? parseFloat(data.rangeMax) : NaN;
  // Parse URL values and use textContent to avoid interpreting them as HTML.
  const gteNum = parseFloat(gte);
  const lteNum = parseFloat(lte);
  const minText =
    gte !== null && !isNaN(gteNum) ? String(gteNum) : String(rangeMin);
  const maxText =
    lte !== null && !isNaN(lteNum)
      ? String(lteNum)
      : String(isNaN(rangeMax) ? 0 : rangeMax);

  // Removing the chip drops only the two price params; everything else stays.
  const removeParams = new URLSearchParams(window.location.search);
  removeParams.delete(ERJ_PRICE_PARAM_MIN);
  removeParams.delete(ERJ_PRICE_PARAM_MAX);
  const removeQuery = removeParams.toString();
  const removeUrl =
    window.location.pathname + (removeQuery ? `?${removeQuery}` : "");

  const chip = document.createElement("div");
  chip.className = "erj-collection-selected-filter-item";
  chip.dataset.erjPriceChip = "";

  const chipText = document.createElement("span");
  chipText.className = "erj-collection-selected-filter-item-text";
  chipText.textContent = `${symbol}${minText}\u00A0-\u00A0${symbol}${maxText}`;
  chip.appendChild(chipText);

  chip.insertAdjacentHTML(
    "beforeend",
    `<svg
      class="selected-filter-item-remove"
      width="6"
      height="6"
      viewBox="0 0 6 6"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line x1="0" y1="0" x2="6" y2="6" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>
      <line x1="6" y1="0" x2="0" y2="6" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>
    </svg>`
  );
  chip
    .querySelector(".selected-filter-item-remove")
    ?.setAttribute("data-url-to-remove", removeUrl);

  container.appendChild(chip);
}

// Restore local prices in chip-removal URLs, preferring the applied range.
// Preview requests can overwrite cached bounds for the same tiers.
function erjUsdEnumToPriceRangeUrl(urlStr) {
  try {
    const shopifyCurrency = (window.Shopify && window.Shopify.currency) || {};
    const activeCurrency = shopifyCurrency.active || "USD";
    if (activeCurrency === "USD") return urlStr;

    const u = new URL(urlStr, window.location.origin);
    const tierValues = u.searchParams.getAll(ERJ_PRICE_USD_PARAM);
    const tiers = tierValues.map(parseFloat).filter(Number.isFinite);
    if (tiers.length === 0) return urlStr;

    const appliedParams = new URLSearchParams(window.location.search);
    const appliedMin = appliedParams.get(ERJ_PRICE_PARAM_MIN);
    const appliedMax = appliedParams.get(ERJ_PRICE_PARAM_MAX);

    // Fall back to cached bounds, then approximate from USD tiers.
    const remembered = erjRecallPriceRange(tierValues);
    let minLocal;
    let maxLocal;
    if (appliedMin !== null || appliedMax !== null) {
      minLocal = appliedMin;
      maxLocal = appliedMax;
    } else if (remembered) {
      const data = window.erjPriceFilterData || {};
      minLocal = remembered.gte != null ? remembered.gte : String(parseFloat(data.rangeMin) || 0);
      maxLocal = remembered.lte != null ? remembered.lte : String(data.rangeMax);
    } else {
      const rate = parseFloat(shopifyCurrency.rate) || 1;
      minLocal = String(Math.max(0, (Math.min.apply(null, tiers) - 2) * rate));
      maxLocal = String(Math.max.apply(null, tiers) * rate);
    }

    u.searchParams.delete(ERJ_PRICE_USD_PARAM);
    u.searchParams.delete(ERJ_PRICE_PARAM_MIN);
    u.searchParams.delete(ERJ_PRICE_PARAM_MAX);
    if (minLocal !== null) u.searchParams.append(ERJ_PRICE_PARAM_MIN, minLocal);
    if (maxLocal !== null) u.searchParams.append(ERJ_PRICE_PARAM_MAX, maxLocal);
    // Return a relative URL because section requests prepend the origin.
    return u.pathname + u.search + u.hash;
  } catch (e) {
    return urlStr;
  }
}

function _bindSelectedFilterRemoveEvents() {
  const selectedFiltersContainer = document.querySelector(
    ".erj-collection-selected-filters"
  );

  if (!selectedFiltersContainer) {
    return;
  }

  selectedFiltersContainer.addEventListener("click", function (event) {
    const filterItem = event.target.closest(".erj-collection-selected-filter-item");

    if (filterItem) {
      const removeIcon = filterItem.querySelector(".selected-filter-item-remove");

      if (!removeIcon) {
        return;
      }

      let urlToRemove = removeIcon?.dataset?.urlToRemove;

      if (
        urlToRemove &&
        typeof urlToRemove === "string" &&
        urlToRemove.trim() !== ""
      ) {
        urlToRemove = erjUsdEnumToPriceRangeUrl(urlToRemove);
        history.replaceState({}, document.title, urlToRemove);
        erjCollectionFilterManager.searchAndRender(urlToRemove);
      } else {
        console.warn(
          "[Collection Filters] Clicked .selected-filter-item-remove, but data-url-to-remove was invalid or missing:",
          urlToRemove
        );
      }
    }
  });
}

// Check whether a shadow collection handles the initial grid refresh.
function erjMarketOverrideActive() {
  const section = document.querySelector("[data-shadow-collection-path]");
  return Boolean(section && section.dataset.shadowCollectionPath);
}

// Re-fetch non-USD price-filtered links once because SSR ignores native price params.
// Shadow collections refresh themselves; skip them to avoid competing requests.
function erjReRenderPriceFilterOnLoad() {
  if (window.erjPriceFilterReRendered) return;

  const shopifyCurrency = (window.Shopify && window.Shopify.currency) || {};
  const activeCurrency = shopifyCurrency.active || "USD";
  if (activeCurrency === "USD") return;

  if (erjMarketOverrideActive()) return;

  const params = new URLSearchParams(window.location.search);
  if (
    params.get(ERJ_PRICE_PARAM_MIN) === null &&
    params.get(ERJ_PRICE_PARAM_MAX) === null
  ) {
    return;
  }

  window.erjPriceFilterReRendered = true;
  erjCollectionFilterManager.searchAndRender(
    window.location.pathname + window.location.search
  );
}

function _erjCollectionInit() {
  try {
    erjCollectionSorterManager = new ErcSorterManager();
    erjCollectionFilterManager = new ErcFilterPanelManager();
    new ErcFilterItemManager();
    _bindSelectedFilterRemoveEvents();
    erjRenderPriceFilterChip();
    erjReRenderPriceFilterOnLoad();
  } catch (error) {
    console.error(
      `[Collection Filters] Initialization error: ${error.message}`
    );
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", _erjCollectionInit);
} else {
  _erjCollectionInit();
}
